import { useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Row, Col } from '@geberit/gdds';

// styles
import styles from './investors-calendar.module.scss';

// components
import InvestorTabs from './investors-tab';
import InvestorDropdown from './investors-dropdown';

// utils
import { fetchEvents } from './actions';
import { currentEventsSelector } from 'utils/selectors/eventsSelector';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useIsDesktop } from 'components/App/SizeProvider';
import { useCurrentLanguage } from 'utils/hooks/useCurrentLanguage';
import { isEmpty } from 'utils/is-empty';
import { useLocale } from 'utils/use-locale';

export interface InvestorsCalendarProps {
  pastEventsTitle: string;
  futureEventsTitle: string;
  backgroundColor?: string;
}

export function InvestorsCalendar({
  pastEventsTitle,
  futureEventsTitle,
  backgroundColor = 'white',
}: Readonly<InvestorsCalendarProps>) {
  const isDesktop = useIsDesktop();
  const lang = useCurrentLanguage();
  const locale = useLocale(lang);
  const events = useSelector(currentEventsSelector);
  const dispatch = useDispatch();
  const Component = isDesktop ? InvestorTabs : InvestorDropdown;

  const currentDate = new Date();
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());
  const years = useRef<number[]>([
    currentYear + 1,
    currentYear,
    currentYear - 1,
    currentYear - 2,
    currentYear - 3,
  ]);

  // fetch events if there are none in the global state
  useEffect(() => {
    if (lang && !isEmpty(years.current) && isEmpty(events) && !events?.[lang]) {
      dispatch(fetchEvents(years.current));
    }
  }, [dispatch, events, lang]);

  if (isEmpty(events)) return null;

  return (
    <div
      className={classNameBuilder(styles.sectionWrapper, styles[`${backgroundColor}Background`])}
    >
      <Container maxContentWidth="78rem">
        <Row className={styles.styledRow}>
          <Col size={[4, 8, 8]}>
            <Component
              events={events}
              futureEventsTitle={futureEventsTitle}
              pastEventsTitle={pastEventsTitle}
              currentYear={currentYear}
              locale={locale}
              language={lang}
              setCurrentYear={setCurrentYear}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
}
